import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { apiClient, createDiagnosis, InitDiagnosisPayload } from '../services/apiClient';
import { DiagnosisReadDTO, DiagnosisCreateDTO } from '../generated/api-client';
import { getAxiosErrorDetail } from '../utils/axiosErrorDetail';
import { DIAGNOSIS_ALL_QUERY_KEY } from './useDiagnosisAll';

export const useDiagnosis = () => {
  return useQuery<DiagnosisReadDTO[]>('diagnosis', () => apiClient.list_items_diagnosis__get());
};

export const useDiagnosisById = (id: string) => {
  return useQuery<DiagnosisReadDTO>(
    ['diagnosis', id],
    () => apiClient.get_item_diagnosis__item_id__get(id),
    {
      enabled: !!id,
    }
  );
};

export const useCreateDiagnosis = () => {
  const queryClient = useQueryClient();

  return useMutation<DiagnosisReadDTO, Error, DiagnosisCreateDTO>(
    data => apiClient.create_item_diagnosis__post(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('diagnosis');
        queryClient.invalidateQueries(DIAGNOSIS_ALL_QUERY_KEY);
      },
    }
  );
};

export const useInitDiagnosis = () => {
  const queryClient = useQueryClient();

  return useMutation<DiagnosisReadDTO, Error, InitDiagnosisPayload>(createDiagnosis, {
    onSuccess: diagnosis => {
      if (diagnosis.id) {
        // Scrittura diretta della diagnosi appena creata: niente bisogno di re-fetchare
        // ['diagnosis', diagnosis.id], i dati sono già quelli giusti.
        queryClient.setQueryData(['diagnosis', diagnosis.id], diagnosis);
      }
      // Bug 6 (performance): `invalidateQueries` senza `exact: true` fa match "fuzzy" per
      // prefisso, quindi invalidare la stringa 'diagnosis'/'diagnosisCustomer'/
      // 'diagnosisCustomerSites' forza anche il refetch di OGNI query per-id già in cache
      // (es. ogni diagnosi/sito/cliente diagnosi visitati in questa sessione), non solo
      // delle liste che devono davvero aggiornarsi dopo la creazione. Con `exact: true`
      // si invalidano solo le liste (che devono comunque includere il nuovo elemento),
      // evitando decine di refetch superflui subito dopo la creazione.
      queryClient.invalidateQueries('diagnosis', { exact: true });
      queryClient.invalidateQueries(DIAGNOSIS_ALL_QUERY_KEY, { exact: true });
      queryClient.invalidateQueries('diagnosisCustomer', { exact: true });
      queryClient.invalidateQueries('diagnosisCustomerSites', { exact: true });
    },
  });
};

export const useUpdateDiagnosis = () => {
  const queryClient = useQueryClient();

  return useMutation<DiagnosisReadDTO, Error, { id: string; data: DiagnosisCreateDTO }>(
    ({ id, data }) => apiClient.update_item_diagnosis__item_id__put(id, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries('diagnosis');
        queryClient.invalidateQueries(DIAGNOSIS_ALL_QUERY_KEY);
        queryClient.invalidateQueries(['diagnosis', variables.id]);
      },
    }
  );
};

export const useDeleteDiagnosis = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, string>(
    id => apiClient.delete_item_diagnosis__item_id__delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('diagnosis');
        queryClient.invalidateQueries(DIAGNOSIS_ALL_QUERY_KEY);
      },
      onError: error => {
        toast.error(getAxiosErrorDetail(error, "Errore nell'eliminazione della diagnosi"));
      },
    }
  );
};
